/**
 * App — Application bootstrap and main loop.
 *
 * Creates the engine runtime: renderer, ECS world, timer, event bus,
 * asset registry, and runs the 7-phase game loop.
 */

import { LoopPhase } from '../shared/types.js';
import { EventBus } from './EventBus.js';
import { EngineTimer } from './Timer.js';
import { Logger } from './Logger.js';
import { World } from '../ecs/World.js';
import { EngineRenderer, type RendererConfig } from '../render/Renderer.js';
import { DebugDraw } from '../render/DebugDraw.js';
import { AssetRegistry } from '../assets/AssetRegistry.js';
import { GLTFPipeline } from '../assets/GLTFPipeline.js';
import { LeakDetector } from '../debug/LeakDetector.js';
import { FIXED_DT, MAX_FIXED_STEPS } from '../shared/constants.js';
import { GPUMemoryTracker } from '../debug/GPUMemoryTracker.js';
import { HotReloadManager } from '../platform/hotreload/HotReloadManager.js';
import type { LargeWorldModule } from './largeworld/LargeWorldModule.js';

export interface AppConfig {
  canvas: HTMLCanvasElement;
  width?: number;
  height?: number;
  pixelRatio?: number;
  antialias?: boolean;
  alpha?: boolean;
  powerPreference?: GPUPowerPreference;
  /** Pre-created WebGL2 context (see RendererConfig.context). */
  context?: WebGL2RenderingContext;
  /** Force the WebGL2 backend even where WebGPU is available. */
  forceWebGL?: boolean;
  /** XR multiview (see RendererConfig.multiview) — off by default. */
  multiview?: boolean;
  /** Reversed floating-point depth, WebGPU only (see RendererConfig.reversedDepth) — off by default. */
  reversedDepth?: boolean;
  /**
   * Request GPU timestamp queries (see RendererConfig.trackTimestamp).
   * Must be set at construction — the backend snapshots it at its own init.
   */
  trackTimestamp?: boolean;
  /**
   * Enable hot-reload support. When true (or when import.meta.env?.DEV is
   * true), a HotReloadManager is created and attached as app.hotReload.
   * Defaults to import.meta.env?.DEV in Vite; false otherwise.
   */
  hotReload?: boolean;
  /** Debounce interval in ms for hot-reload. Default: 200. */
  hotReloadDebounceMs?: number;
  /**
   * Record per-phase CPU time each frame, readable via `app.phaseTimings`.
   * Off by default: when disabled the loop takes the original code path and
   * pays nothing (no timer reads, no map writes).
   */
  profilePhases?: boolean;
}

/** Human-readable phase names, indexed by LoopPhase, for timing snapshots. */
const PHASE_NAMES: Record<number, string> = {
  [LoopPhase.Input]: 'Input',
  [LoopPhase.FixedSim]: 'FixedSim',
  [LoopPhase.Animation]: 'Animation',
  [LoopPhase.RenderSub]: 'RenderSub',
  [LoopPhase.Render]: 'Render',
  [LoopPhase.Post]: 'Post',
  [LoopPhase.UI]: 'UI',
};

/**
 * Per-phase CPU time accumulator, milliseconds.
 *
 * FixedSim runs 0..N times per render frame, so samples ACCUMULATE within a
 * frame and reset on beginFrame() — reporting only the last FixedSim step
 * would understate the phase whenever the accumulator catches up.
 */
export class PhaseTimings {
  private readonly _ms = new Map<string, number>();

  beginFrame(): void {
    this._ms.clear();
  }

  record(phase: number, ms: number): void {
    const name = PHASE_NAMES[phase] ?? String(phase);
    this._ms.set(name, (this._ms.get(name) ?? 0) + ms);
  }

  snapshot(): ReadonlyMap<string, number> {
    return this._ms;
  }
}

export interface IModule {
  readonly name: string;
  init(app: App): void | Promise<void>;
  dispose?(): void;
}

export interface DebugTools {
  readonly draw: DebugDraw;
  readonly leaks: LeakDetector;
  readonly gpuMemory: GPUMemoryTracker;
}

export class App {
  readonly events: EventBus;
  readonly timer: EngineTimer;
  readonly world: World;
  readonly renderer: EngineRenderer;
  readonly assets: AssetRegistry;
  readonly gltf: GLTFPipeline;
  readonly debug: DebugDraw;
  readonly debugTools: DebugTools;
  /**
   * Hot-reload manager. Only defined when hot-reload is enabled (dev mode).
   * Use `app.hotReload?.register(...)` to safely access in code that runs
   * in both dev and production.
   */
  readonly hotReload: HotReloadManager | undefined;

  /**
   * Large-world FP64 origin-rebase module.
   * Populated when app.use(new LargeWorldModule(...)) is registered.
   */
  largeWorld: LargeWorldModule | null = null;

  private modules: IModule[] = [];
  private running = false;
  private _starting = false;
  private _started = false;
  private _disposed = false;

  private readonly _phaseTimings = new PhaseTimings();
  private _profilePhases = false;

  /**
   * Per-phase CPU milliseconds for the most recent frame.
   * Always empty unless `profilePhases` was enabled at construction.
   * This is a LIVE VIEW into the internal accumulator, valid only until the
   * next `beginFrame()` call clears it — do not retain the returned map
   * across frames; snapshot the entries you need instead.
   */
  get phaseTimings(): ReadonlyMap<string, number> {
    return this._phaseTimings.snapshot();
  }

  private constructor(
    renderer: EngineRenderer,
    events: EventBus,
    hotReloadEnabled: boolean,
    hotReloadDebounceMs: number,
  ) {
    this.events = events;
    this.timer = new EngineTimer();
    this.world = new World(events);
    this.renderer = renderer;
    this.assets = new AssetRegistry();
    this.gltf = new GLTFPipeline();
    this.debug = new DebugDraw(this.world.scene);

    // Debug tools
    const leaks = new LeakDetector();
    const gpuMemory = new GPUMemoryTracker();
    this.debugTools = {
      draw: this.debug,
      leaks,
      gpuMemory,
    };

    // Wire asset auto-disposal via events
    this.assets.bindToEventBus(events);
    this.assets.setLeakDetector(leaks);

    // Hot-reload (dev-only, opt-in)
    if (hotReloadEnabled) {
      this.hotReload = new HotReloadManager(hotReloadDebounceMs);
      this.hotReload.enable();
      Logger.info('App', 'Hot-reload enabled');
    } else {
      this.hotReload = undefined;
    }
  }

  static async create(config: AppConfig): Promise<App> {
    const events = new EventBus();

    const rendererConfig: RendererConfig = {
      canvas: config.canvas,
      antialias: config.antialias,
      alpha: config.alpha,
      powerPreference: config.powerPreference,
      pixelRatio: config.pixelRatio,
      context: config.context,
      forceWebGL: config.forceWebGL,
      multiview: config.multiview,
      trackTimestamp: config.trackTimestamp,
      reversedDepth: config.reversedDepth,
    };

    const renderer = await EngineRenderer.create(rendererConfig);

    const width = config.width ?? window.innerWidth;
    const height = config.height ?? window.innerHeight;
    renderer.resize(width, height);

    // Determine if hot-reload should be enabled.
    // Priority: explicit config.hotReload > import.meta.env?.DEV > false
    const viteIsDev = (() => {
      try {
        const meta = import.meta as unknown as { env?: { DEV?: boolean } };
        return meta.env !== undefined && meta.env.DEV === true;
      } catch {
        return false;
      }
    })();
    const hotReloadEnabled = config.hotReload ?? viteIsDev;
    const hotReloadDebounceMs = config.hotReloadDebounceMs ?? 200;

    const app = new App(renderer, events, hotReloadEnabled, hotReloadDebounceMs);
    app._profilePhases = config.profilePhases ?? false;
    // The GPU memory tracker was constructed but never init()ed, so
    // app.debugTools.gpuMemory.snapshot() reported 0 MB forever. Best-effort:
    // the WebGL2 fallback has no GPUDevice and the tracker stays disabled.
    const device = (renderer.native as unknown as { backend?: { device?: GPUDevice } }).backend?.device;
    void app.debugTools.gpuMemory.init(device).catch(() => { /* optional dependency */ });

    // Initialize glTF pipeline (needs renderer for KTX2 support detection)
    app.gltf.init(renderer.native);

    Logger.info('App', `EngineX initialized (${width}x${height})`);

    return app;
  }

  /**
   * Register a module. Must be called before start().
   */
  use(module: IModule): this {
    if (this._started || this._starting) {
      throw new Error(`Module "${module.name}" registered after start() — call use() before start()`);
    }
    this.modules.push(module);
    return this;
  }

  /**
   * Register AND initialize a module on an already-started app — the
   * deferred-boot path (e.g. a game that boots a light menu shell first and
   * loads the heavy world modules only when the player commits to entering).
   * Mirrors start()'s init exactly (`await module.init(this)`), then calls
   * `init` on ONLY the systems this module just registered — never
   * `world.initSystems()`, which would re-run `init` on every pre-existing
   * system (doubled listeners, reset state).
   */
  async useLate(module: IModule): Promise<void> {
    if (!this._started) {
      throw new Error(`useLate("${module.name}") before start() — use use()`);
    }
    const sched = this.world.scheduler as unknown as { systems: Array<{ name: string; init?(w: unknown): void }> };
    const before = new Set(sched.systems);
    this.modules.push(module);
    await module.init(this);
    for (const sys of sched.systems) {
      if (before.has(sys)) continue;
      try {
        sys.init?.(this.world);
      } catch (err) {
        Logger.error('App', `Late system "${sys.name}" init failed: ${err}`);
      }
    }
    Logger.info('App', `Module initialized (late): ${module.name}`);
  }

  /**
   * Look up a registered module by its `name`. Returns `undefined` if no
   * matching module has been registered yet. Prefer this over reaching into
   * private state from outside `App`.
   */
  getModule<T extends IModule = IModule>(name: string): T | undefined {
    return this.modules.find((m) => m.name === name) as T | undefined;
  }

  /**
   * Start the game loop.
   */
  async start(): Promise<void> {
    if (this._started || this._starting) {
      Logger.warn('App', 'start() called more than once — ignoring');
      return;
    }
    this._starting = true;

    try {
      // Initialize modules
      for (const mod of this.modules) {
        await mod.init(this);
        Logger.info('App', `Module initialized: ${mod.name}`);
      }

      // Initialize ECS systems
      this.world.initSystems();
    } catch (err) {
      this._starting = false;
      throw err;
    }

    // Mark as started only after successful initialization
    this._started = true;

    this.running = true;

    // Start the render loop.
    // When an XR session is active, Three.js passes (timestamp, XRFrame)
    // to the callback. We expose the XRFrame via globalThis.__xrFrame so
    // XR systems (HitTest, Anchor, Depth, LightProbe) can read it.
    this.renderer.native.setAnimationLoop((_time?: number, frame?: unknown) => {
      if (frame) (globalThis as any).__xrFrame = frame;
      try {
        this.tick();
      } catch (err) {
        // Include the stack: a message-only line ("Invalid value used as weak
        // map key") is undiagnosable from a devtools-less device beacon.
        Logger.error('App', `Unhandled error in tick(): ${(err as Error)?.stack ?? err}`);
      } finally {
        if (frame) (globalThis as any).__xrFrame = undefined;
      }
    });

    Logger.info('App', 'Game loop started');
  }

  /**
   * Stop the game loop.
   */
  stop(): void {
    this.running = false;
    this.renderer.native.setAnimationLoop(null);
    Logger.info('App', 'Game loop stopped');
  }

  /**
   * Run a phase, recording its CPU time when profiling is enabled.
   * The disabled path is a direct call — no timer reads, no allocation.
   */
  private _runPhase(phase: LoopPhase, dt: number): void {
    if (!this._profilePhases) {
      this.world.runPhase(phase, dt);
      return;
    }
    const t0 = performance.now();
    this.world.runPhase(phase, dt);
    this._phaseTimings.record(phase, performance.now() - t0);
  }

  /**
   * Main loop tick — executes all 7 phases.
   */
  private tick(): void {
    if (!this.running) return;

    const { rawDt: frameRawDt, fixedSteps } = this.timer.tick();
    if (this._profilePhases) this._phaseTimings.beginFrame();
    // Variable phases get the SAME clamp the fixed accumulator applies
    // (MAX_FIXED_STEPS × FIXED_DT): a 3 s stall (GLB parse, shader compile)
    // previously jumped every mixer/particle emitter three seconds in one
    // frame while Lua's time.dt reported the clamped value — two truths.
    const rawDt = Math.min(frameRawDt, MAX_FIXED_STEPS * FIXED_DT);

    // Phase 1: Input
    this._runPhase(LoopPhase.Input, rawDt);

    // Phase 2: Fixed Simulation (0..N iterations)
    for (let i = 0; i < fixedSteps; i++) {
      this._runPhase(LoopPhase.FixedSim, this.timer.fixedDt);
    }

    // Phase 3: Animation (variable dt)
    this._runPhase(LoopPhase.Animation, rawDt);

    // Phase 4: Render Submission
    this._runPhase(LoopPhase.RenderSub, rawDt);

    // Flush debug draw before render
    this.debug.flush();

    // Phase 5: Render — run the phase's ECS systems BEFORE the draw. Systems here
    // prepare the draw itself (XRCameraOverride suppresses the camera stack during
    // XR presentation; XRLayerSystem submits compositor layers); skipping this call
    // left every LoopPhase.Render system registered but never executed.
    this._runPhase(LoopPhase.Render, rawDt);
    this.renderer.renderFrame(this.world.scene);

    // Phase 6: Post-processing
    this._runPhase(LoopPhase.Post, rawDt);

    // Clear debug draw after render
    this.debug.clear();

    // Phase 7: UI
    this._runPhase(LoopPhase.UI, rawDt);
  }

  /**
   * Get the post-processing pipeline (if created).
   */
  get postProcessing() {
    return this.renderer.postProcessing;
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;

    this.stop();

    // Dispose modules in REVERSE registration order (LIFO). Modules
    // registered later may hold references to resources owned by modules
    // registered earlier (e.g. an XR module holding a renderer ref). Tearing
    // down in forward order would dispose A while B still depends on it,
    // and B's dispose would then crash or silently corrupt.
    for (let i = this.modules.length - 1; i >= 0; i--) {
      const mod = this.modules[i];
      try {
        mod.dispose?.();
      } catch (err) {
        Logger.error('App', `Module "${mod.name}" dispose failed: ${err}`);
      }
    }
    this.modules.length = 0;

    this.timer.dispose();
    this.world.dispose();
    this.debug.dispose();
    this.debugTools.leaks.dispose();
    this.gltf.dispose();
    this.assets.dispose();
    this.hotReload?.dispose();
    this.renderer.dispose();
    this.events.clear();

    Logger.info('App', 'Disposed');
  }
}
